import { notificationsService } from './notificationsService';
import { supabase } from '../lib/supabase';

/**
 * Notification Triggers Service
 * Centralized service for triggering notifications across the application
 * Connects all user actions and system events to the notifications hub
 */

/**
 * Trigger notification for application status update
 * @param {string} applicationId - The application ID
 * @param {string} newStatus - The new status
 * @param {Object} applicationData - Additional application data
 */
export async function triggerApplicationStatusNotification(applicationId, newStatus, applicationData = {}) {
  try {
    const { jobTitle, companyName, applicantId } = applicationData;
    
    const statusMessages = {
      pending: {
        title: 'Application Submitted',
        message: `Your application for ${jobTitle} at ${companyName} has been submitted successfully.`,
        priority: 'medium'
      },
      reviewed: {
        title: 'Application Under Review',
        message: `Good news! ${companyName} is reviewing your application for ${jobTitle}.`,
        priority: 'high'
      },
      interview: {
        title: 'Interview Invitation',
        message: `Congratulations! ${companyName} wants to interview you for ${jobTitle}.`,
        priority: 'high'
      },
      offer: {
        title: 'Job Offer Received',
        message: `Exciting news! ${companyName} has extended an offer for ${jobTitle}.`,
        priority: 'high'
      },
      accepted: {
        title: 'Application Accepted',
        message: `Your application for ${jobTitle} at ${companyName} has been accepted!`,
        priority: 'high'
      },
      rejected: {
        title: 'Application Status Update',
        message: `${companyName} has updated your application status for ${jobTitle}.`,
        priority: 'medium'
      }
    };

    const notificationData = statusMessages?.[newStatus] || statusMessages?.pending;

    await notificationsService?.create({
      type: 'application_status',
      priority: notificationData?.priority,
      title: notificationData?.title,
      message: notificationData?.message,
      actionUrl: `/applications/${applicationId}`,
      actionLabel: 'View Application',
      relatedApplicationId: applicationId,
      metadata: {
        status: newStatus,
        jobTitle,
        companyName
      }
    });

    return { success: true };
  } catch (error) {
    console.error('Error triggering application status notification:', error);
    return { success: false, error };
  }
}

/**
 * Trigger notification for new job recommendations
 * @param {string} userId - The user ID
 * @param {Array} recommendations - Array of new recommendations
 */
export async function triggerJobRecommendationNotifications(userId, recommendations) {
  try {
    if (!recommendations || recommendations?.length === 0) return;

    // Group recommendations by match score to determine priority
    const highMatchRecs = recommendations?.filter(r => r?.matchScore >= 85);
    
    if (highMatchRecs?.length > 0) {
      const topRec = highMatchRecs?.[0];
      
      await notificationsService?.create({
        type: 'job_recommendation',
        priority: 'high',
        title: 'Perfect Job Match Found!',
        message: `We found ${highMatchRecs?.length} highly matching job${highMatchRecs?.length > 1 ? 's' : ''} for you. Check out "${topRec?.jobTitle}" with a ${topRec?.matchScore}% match!`,
        actionUrl: '/dashboard/jobs?filter=recommendations',
        actionLabel: 'View Recommendations',
        relatedJobId: topRec?.jobId,
        metadata: {
          recommendationCount: recommendations?.length,
          topMatchScore: topRec?.matchScore,
          topJobTitle: topRec?.jobTitle
        }
      });
    } else {
      await notificationsService?.create({
        type: 'job_recommendation',
        priority: 'medium',
        title: 'New Job Recommendations',
        message: `${recommendations?.length} new job recommendations are waiting for you based on your profile.`,
        actionUrl: '/dashboard/jobs?filter=recommendations',
        actionLabel: 'View All',
        metadata: {
          recommendationCount: recommendations?.length
        }
      });
    }

    return { success: true };
  } catch (error) {
    console.error('Error triggering job recommendation notifications:', error);
    return { success: false, error };
  }
}

/**
 * Trigger notification for saved job updates
 * @param {string} jobId - The job ID
 * @param {string} updateType - Type of update (status_change, deadline_approaching, salary_update)
 * @param {Object} jobData - Job data
 */
export async function triggerSavedJobUpdateNotification(jobId, updateType, jobData = {}) {
  try {
    // Get all users who saved this job
    const { data: savedByUsers, error } = await supabase?.from('saved_jobs')?.select('user_id')?.eq('job_id', jobId);

    if (error) throw error;

    const { jobTitle, companyName, updateDetails } = jobData;

    const updateMessages = {
      status_change: {
        title: 'Saved Job Update',
        message: `The status of "${jobTitle}" at ${companyName} has been updated. Check it out before it's too late!`,priority: 'medium'
      },
      deadline_approaching: {
        title: 'Application Deadline Approaching',
        message: `Only ${updateDetails?.daysLeft} days left to apply for "${jobTitle}" at ${companyName}!`,
        priority: 'high'
      },
      salary_update: {
        title: 'Salary Range Updated',
        message: `The salary range for "${jobTitle}" at ${companyName} has been updated.`,
        priority: 'medium'
      },
      new_benefits: {
        title: 'Benefits Updated',
        message: `${companyName} has updated the benefits package for "${jobTitle}".`,
        priority: 'low'
      }
    };

    const notificationData = updateMessages?.[updateType] || updateMessages?.status_change;

    // Create notification for each user who saved the job
    const notificationPromises = savedByUsers?.map(async ({ user_id }) => {
      // Skip notification if user already applied
      const { data: application } = await supabase?.from('job_applications')?.select('id')?.eq('user_id', user_id)?.eq('job_id', jobId)?.single();

      if (application) return null; // Don't notify if already applied

      return notificationsService?.create({
        type: 'saved_job_update',
        priority: notificationData?.priority,
        title: notificationData?.title,
        message: notificationData?.message,
        actionUrl: `/jobs/${jobId}`,
        actionLabel: 'View Job',
        relatedJobId: jobId,
        metadata: {
          updateType,
          jobTitle,
          companyName,
          ...updateDetails
        }
      });
    });

    await Promise.all(notificationPromises);

    return { success: true, notifiedUsers: savedByUsers?.length };
  } catch (error) {
    console.error('Error triggering saved job update notifications:', error);
    return { success: false, error };
  }
}

/**
 * Trigger notification for profile views
 * @param {string} profileUserId - The user whose profile was viewed
 * @param {Object} viewerData - Data about who viewed the profile
 */
export async function triggerProfileViewNotification(profileUserId, viewerData = {}) {
  try {
    const { viewerName, viewerCompany, viewerRole, isRecruiter } = viewerData;

    let message = `Someone viewed your profile`;
    if (isRecruiter && viewerCompany) {
      message = `A recruiter from ${viewerCompany} viewed your profile`;
    } else if (viewerName) {
      message = `${viewerName} viewed your profile`;
    }

    await notificationsService?.create({
      type: 'profile_view',
      priority: isRecruiter ? 'high' : 'medium',
      title: 'Profile View',
      message,
      actionUrl: '/dashboard/profile/analytics',
      actionLabel: 'View Analytics',
      metadata: {
        viewerName,
        viewerCompany,
        viewerRole,
        isRecruiter,
        viewedAt: new Date()?.toISOString()
      }
    });

    return { success: true };
  } catch (error) {
    console.error('Error triggering profile view notification:', error);
    return { success: false, error };
  }
}

/**
 * Trigger notification for interview scheduling
 * @param {string} applicationId - The application ID
 * @param {Object} interviewData - Interview details
 */
export async function triggerInterviewScheduleNotification(applicationId, interviewData = {}) {
  try {
    const { jobTitle, companyName, interviewDate, interviewType, applicantId } = interviewData;
    
    const dateStr = new Date(interviewDate)?.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    await notificationsService?.create({
      type: 'interview_scheduled',
      priority: 'high',
      title: 'Interview Scheduled',
      message: `Your ${interviewType || 'interview'} with ${companyName} for ${jobTitle} is scheduled for ${dateStr}.`,
      actionUrl: `/applications/${applicationId}`,
      actionLabel: 'View Details',
      relatedApplicationId: applicationId,
      metadata: {
        jobTitle,
        companyName,
        interviewDate,
        interviewType
      }
    });

    return { success: true };
  } catch (error) {
    console.error('Error triggering interview schedule notification:', error);
    return { success: false, error };
  }
}

/**
 * Trigger notification for interview reminder (24 hours before)
 * @param {string} applicationId - The application ID
 * @param {Object} interviewData - Interview details
 */
export async function triggerInterviewReminderNotification(applicationId, interviewData = {}) {
  try {
    const { jobTitle, companyName, interviewDate, interviewType } = interviewData;

    await notificationsService?.create({
      type: 'interview_reminder',
      priority: 'high',
      title: 'Interview Reminder',
      message: `Reminder: Your ${interviewType || 'interview'} with ${companyName} for ${jobTitle} is tomorrow!`,
      actionUrl: `/applications/${applicationId}`,
      actionLabel: 'Prepare Now',
      relatedApplicationId: applicationId,
      expiresAt: interviewDate, // Notification expires after interview time
      metadata: {
        jobTitle,
        companyName,
        interviewDate,
        interviewType
      }
    });

    return { success: true };
  } catch (error) {
    console.error('Error triggering interview reminder notification:', error);
    return { success: false, error };
  }
}

/**
 * Trigger notification for message received
 * @param {string} recipientId - The user receiving the message
 * @param {Object} messageData - Message details
 */
export async function triggerMessageNotification(recipientId, messageData = {}) {
  try {
    const { senderName, senderCompany, messagePreview, conversationId } = messageData;

    await notificationsService?.create({
      type: 'message',
      priority: 'medium',
      title: 'New Message',
      message: `${senderName}${senderCompany ? ` from ${senderCompany}` : ''}: ${messagePreview}`,
      actionUrl: `/messages/${conversationId}`,
      actionLabel: 'Read Message',
      metadata: {
        senderName,
        senderCompany,
        conversationId
      }
    });

    return { success: true };
  } catch (error) {
    console.error('Error triggering message notification:', error);
    return { success: false, error };
  }
}

/**
 * Trigger notification for application deadline reminder
 * @param {string} jobId - The job ID
 * @param {Object} jobData - Job data
 * @param {number} daysUntilDeadline - Days until application deadline
 */
export async function triggerApplicationDeadlineNotification(jobId, jobData = {}, daysUntilDeadline) {
  try {
    // Get users who saved this job but haven't applied
    const { data: savedByUsers, error } = await supabase?.from('saved_jobs')?.select('user_id')?.eq('job_id', jobId);

    if (error) throw error;

    const { jobTitle, companyName } = jobData;

    const notificationPromises = savedByUsers?.map(async ({ user_id }) => {
      // Check if user has already applied
      const { data: application } = await supabase?.from('job_applications')?.select('id')?.eq('user_id', user_id)?.eq('job_id', jobId)?.single();

      if (application) return null; // Don't notify if already applied

      return notificationsService?.create({
        type: 'application_deadline',
        priority: daysUntilDeadline <= 2 ? 'high' : 'medium',
        title: 'Application Deadline Reminder',
        message: `${daysUntilDeadline} day${daysUntilDeadline !== 1 ? 's' : ''} left to apply for "${jobTitle}" at ${companyName}!`,
        actionUrl: `/jobs/${jobId}`,
        actionLabel: 'Apply Now',
        relatedJobId: jobId,
        metadata: {
          jobTitle,
          companyName,
          daysUntilDeadline
        }
      });
    });

    await Promise.all(notificationPromises);

    return { success: true, notifiedUsers: savedByUsers?.length };
  } catch (error) {
    console.error('Error triggering application deadline notifications:', error);
    return { success: false, error };
  }
}

/**
 * Trigger notification for support ticket creation
 * @param {string} ticketId - The ticket ID
 * @param {Object} ticketData - Ticket data
 */
export async function triggerTicketCreatedNotification(ticketId, ticketData = {}) {
  try {
    const { subject, userId } = ticketData;
    
    await notificationsService?.create({
      userId,
      type: 'ticket_created',
      title: 'Support Ticket Created',
      message: `Your support ticket "${subject}" has been created. Our team will respond shortly.`,
      priority: 'medium',
      relatedEntityType: 'ticket',
      relatedEntityId: ticketId
    });
  } catch (error) {
    console.error('Error triggering ticket created notification:', error);
  }
}

/**
 * Trigger notification for ticket status update
 * @param {string} ticketId - The ticket ID
 * @param {Object} ticketData - Ticket data
 */
export async function triggerTicketStatusUpdateNotification(ticketId, ticketData = {}) {
  try {
    const { subject, status, userId } = ticketData;
    
    const statusMessages = {
      assigned: {
        title: 'Ticket Assigned',
        message: `Your ticket "${subject}" has been assigned to a support agent.`,
        priority: 'medium'
      },
      in_progress: {
        title: 'Ticket In Progress',
        message: `Our team is working on your ticket "${subject}".`,
        priority: 'medium'
      },
      pending_user: {
        title: 'Response Required',
        message: `We need more information about your ticket "${subject}".`,
        priority: 'high'
      },
      resolved: {
        title: 'Ticket Resolved',
        message: `Your ticket "${subject}" has been resolved.`,
        priority: 'high'
      },
      closed: {
        title: 'Ticket Closed',
        message: `Your ticket "${subject}" has been closed.`,
        priority: 'medium'
      }
    };

    const notification = statusMessages?.[status];
    if (notification) {
      await notificationsService?.create({
        userId,
        type: 'ticket_status_update',
        title: notification?.title,
        message: notification?.message,
        priority: notification?.priority,
        relatedEntityType: 'ticket',
        relatedEntityId: ticketId
      });
    }
  } catch (error) {
    console.error('Error triggering ticket status notification:', error);
  }
}

/**
 * Trigger notification for new ticket message
 * @param {string} ticketId - The ticket ID
 * @param {Object} messageData - Message data
 */
export async function triggerTicketMessageNotification(ticketId, messageData = {}) {
  try {
    const { subject, senderName, recipientId, isInternal } = messageData;
    
    // Don't notify for internal notes
    if (isInternal) return;
    
    await notificationsService?.create({
      userId: recipientId,
      type: 'ticket_message',
      title: 'New Message on Your Ticket',
      message: `${senderName} replied to your ticket "${subject}".`,
      priority: 'high',
      relatedEntityType: 'ticket',
      relatedEntityId: ticketId
    });
  } catch (error) {
    console.error('Error triggering ticket message notification:', error);
  }
}

export default {
  triggerApplicationStatusNotification,
  triggerJobRecommendationNotifications,
  triggerSavedJobUpdateNotification,
  triggerProfileViewNotification,
  triggerInterviewScheduleNotification,
  triggerInterviewReminderNotification,
  triggerMessageNotification,
  triggerApplicationDeadlineNotification,
  triggerTicketCreatedNotification,
  triggerTicketStatusUpdateNotification,
  triggerTicketMessageNotification
};
function notificationTriggers(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: notificationTriggers is not implemented yet.', args);
  return null;
}

export { notificationTriggers };